/**
 * A figure the sketch contradicts (E-64, design 2026-10-28 §3.2), as the
 * board answers it: the server's sentence — „Wand 4: 17,50 m — die Skizze
 * zeichnet sie etwa 1,0 m lang. Komma verrutscht?“ — and the three ways out
 * beside it. Each comma shift the sketch fits, „Stimmt so“ and „Maß
 * eintippen“. The system offers, the person chooses (E-16); nothing is
 * applied without a press.
 *
 * Also the two rules a replay alone can make about such a wall, and what the
 * board does with them until the next replay answer.
 */
import { useState } from "react";

import type { DemoRunResult } from "@/api";
import { Button } from "@/components/Button";
import { ConfirmReleaseLoss } from "@/components/ConfirmReleaseLoss";
import { AlertIcon } from "@/components/icons";
import type { FacadeEditability } from "@/lib/facadeEditability";
import { formatMetres } from "@/lib/format";
import { MEASURE_IMPLAUSIBLE, type DemoFinding, type DemoOutline } from "@/lib/outline";

import styles from "./ImplausibleMeasure.module.css";
import { measureSummary, useMeasureWrite, type MeasureGesture, type MeasureTarget } from "./measureWrite";

/** The finding: a stated figure the sketch contradicts by more than a factor of five. */
export { MEASURE_IMPLAUSIBLE };
/** The finding: gratings stored on such a wall, held off the plan by the server (blocking). */
export const PLACEMENTS_ON_IMPLAUSIBLE_WALL = "placements_on_implausible_wall";

/**
 * The rules only a replay of the Vorgang makes (`GET …/plan`): the stateless
 * `/demo/plan/manual` the board asks after every gesture knows the walls as
 * the outline hands them over — without the figure in question — and answers
 * without them. Dropped from that answer, they would vanish after the first
 * re-plan while nothing about the wall had changed.
 */
const REPLAY_RULES: ReadonlySet<string> = new Set([MEASURE_IMPLAUSIBLE, PLACEMENTS_ON_IMPLAUSIBLE_WALL]);

/**
 * The findings the board shows: its own answer's, with the replay-only rules
 * taken from the last replay answer instead — kept until the next one, which
 * is the one that can say the figure was decided.
 */
export function withReplayFindings(shown: DemoRunResult | null, replay: DemoRunResult | null): DemoFinding[] {
  const own = (shown?.outlineFindings ?? []).filter((finding) => !REPLAY_RULES.has(finding.rule));
  const kept = (replay?.outlineFindings ?? []).filter((finding) => REPLAY_RULES.has(finding.rule));
  return [...own, ...kept];
}

/**
 * The gratings the server held off the plan because their wall's figure is
 * in question (`placements_on_implausible_wall`). Left out of every plan the
 * board asks for — the planner refuses a grating on a wall without a figure
 * (422) — and kept in the store, so every Stand still states them: they are
 * the person's, and they come back once the figure is decided.
 */
export function heldOffPlacementIds(replay: DemoRunResult | null): ReadonlySet<string> {
  return new Set(
    (replay?.outlineFindings ?? [])
      .filter((finding) => finding.rule === PLACEMENTS_ON_IMPLAUSIBLE_WALL)
      .flatMap((finding) => finding.placementIds ?? []),
  );
}

/** The wall a {@link MEASURE_IMPLAUSIBLE} finding is about, as the board holds it. */
export interface DisputedWall {
  /** Where the board draws it — the wall „Maß eintippen“ picks, and its name. */
  index: number;
  /** The document's own id — what the ops are keyed by. */
  wallId: string;
  /** The stated figure in question. */
  figureMm: number;
}

/**
 * The board's own segment at the finding's index. The replay already counts
 * the finding in the board's numbering — on a chamfered ring every slant
 * before the wall shifts it one further (`plan_from_replay`, `position_of`)
 * — so the index is read against the board and never against the document,
 * whose numbering has no slants: there it names a different wall. The
 * segment carries both the wall's document id (what the ops are keyed by)
 * and the figure in question. `null` where the board has no such wall, or
 * holds no figure in question for it.
 */
export function disputedWallOf(finding: DemoFinding, outline: DemoOutline | null): DisputedWall | null {
  if (outline === null || finding.wallIndex == null) return null;
  const segment = outline.segments[finding.wallIndex];
  const wallId = segment?.wallId ?? null;
  const figureMm = segment?.implausibleLengthMm ?? null;
  return wallId !== null && figureMm !== null ? { index: finding.wallIndex, wallId, figureMm } : null;
}

/** Everything a gesture here needs to be written — the board's own handlers, as „Maß“ gets them. */
export type ImplausibleWriteTarget = Omit<MeasureTarget, "wallId" | "wallName" | "onSaved"> & {
  /** The new Stand is written, by the board's numbering of the wall. */
  onSaved: (versionNo: number, wallIndex: number) => void;
  /** Whether a gesture ends a release first — asked as „Maß“ asks it. */
  editability: FacadeEditability;
  approval: { by: string | null; at: string } | null;
};

interface ImplausibleMeasureProps {
  finding: DemoFinding;
  wall: DisputedWall | null;
  /** `null` where nothing may be written (no Vorgang, the offer out, cancelled): the sentence alone. */
  target: ImplausibleWriteTarget | null;
  /** „Maß eintippen“: pick the wall and put the focus into its „Maß“. */
  onType: (wallIndex: number) => void;
}

/** One finding, as a row of the board's „Zur Kenntnis“. */
export function ImplausibleMeasure({ finding, wall, target, onType }: ImplausibleMeasureProps) {
  return (
    <li className={styles.row} data-role="implausible-measure">
      <span className={styles.text}>{finding.message}</span>
      {wall !== null && target !== null ? (
        <Answers wall={wall} suggestions={finding.suggestedLengthsMm ?? []} target={target} onType={onType} />
      ) : null}
    </li>
  );
}

function Answers({
  wall,
  suggestions,
  target,
  onType,
}: {
  wall: DisputedWall;
  suggestions: number[];
  target: ImplausibleWriteTarget;
  onType: (wallIndex: number) => void;
}) {
  const wallName = `Wand ${wall.index + 1}`;
  const write = useMeasureWrite({
    ...target,
    wallId: wall.wallId,
    wallName,
    onSaved: (versionNo) => target.onSaved(versionNo, wall.index),
  });
  const [busy, setBusy] = useState(false);
  /** Written: the row goes with the finding once the replay answers — until then nothing is pressed twice. */
  const [saved, setSaved] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [asking, setAsking] = useState<MeasureGesture | null>(null);

  async function send(gesture: MeasureGesture): Promise<void> {
    setBusy(true);
    setRefusal(null);
    const outcome = await write(gesture);
    if (outcome.kind === "saved") setSaved(true);
    if (outcome.kind === "invalid" || outcome.kind === "refused") setRefusal(outcome.message);
    setBusy(false);
  }

  function request(gesture: MeasureGesture): void {
    if (busy || saved) return;
    if (target.editability === "approved") {
      setAsking(gesture);
      return;
    }
    void send(gesture);
  }

  const held = busy || saved;
  return (
    <>
      <span className={styles.actions}>
        {suggestions.map((mm) => (
          <Button
            key={mm}
            variant="leise"
            disabled={held}
            // The name in one piece: the figure stands in its own span for the
            // mono face, and a browser joining the two may lose it — „1,75 m
            // übernehmen“, never „übernehmen“ (screen walk, 28.10.2026).
            aria-label={`${formatMetres(mm)} übernehmen`}
            onClick={() => request({ kind: "shift", mm, fromMm: wall.figureMm })}
          >
            <span className="num">{formatMetres(mm)}</span> übernehmen
          </Button>
        ))}
        <Button variant="leise" disabled={held} onClick={() => request({ kind: "confirm", mm: wall.figureMm })}>
          Stimmt so
        </Button>
        <Button variant="leise" disabled={held} onClick={() => onType(wall.index)}>
          Maß eintippen
        </Button>
      </span>
      {/* One region for every answer, mounted before the first one: a region
          that arrives already full is not reliably read out. */}
      <span role="status" className={`${styles.outcome} ${refusal !== null ? styles.outcomeRefused : ""}`}>
        {held ? (
          "Wird neu gezeichnet …"
        ) : refusal !== null ? (
          <>
            <AlertIcon size={14} className={styles.outcomeIcon} />
            {refusal}
          </>
        ) : null}
      </span>
      {asking !== null ? (
        <ConfirmReleaseLoss
          by={target.approval?.by ?? null}
          at={target.approval?.at ?? null}
          subtitle={measureSummary(wallName, asking)}
          onCancel={() => setAsking(null)}
          onConfirm={() => {
            const gesture = asking;
            setAsking(null);
            void send(gesture);
          }}
        />
      ) : null}
    </>
  );
}
